import {
  IonButton,
  IonCard,
  IonCardContent,
  IonItem,
  IonLabel,
  IonList,
  IonSelect,
  IonSelectOption,
  useIonRouter,
} from "@ionic/react";
import React, { useContext, useState, useEffect, useRef } from "react";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";
import CardExpandableHeader from "../components/CardExpandableHeader";
import { getCurrentPlant } from "../util/ApiOptionsHelper";
import { getColumnsWithGroup } from "../api/Windows";
import { encodeParam } from "../util/ApiHelper";
import { StoresProps } from "../models/stores/Stores";
import { ColumnsWithGroupAndFilteredFields } from "../models/windows/GetColumnsWithGroup";
import {
  getCascadeDependencyMap,
  getDependentObjects,
} from "../util/GetColumnsWithGroupHelper";
import { getStoreAccountCascadeValues } from "../util/StoresAccountHelper";
import { CascadeValue } from "../models/analysiscodes/AnalysisCodes";

const StoresAccount: React.FC<StoresProps> = (props) => {
  const { translations } = useContext(TranslationsContext);
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const { expanded, setExpanded, storesTab } = props;
  const router = useIonRouter();
  const [metadataError, setMetadataError] = useState(false);
  const [metadata, setMetadata] = useState<ColumnsWithGroupAndFilteredFields[]>(
    []
  );
  const [values, setValues] = useState<{ [key: string]: any }>({});
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [cascadeValues, setCascadeValues] = useState<{
    [key: string]: CascadeValue[];
  }>({});

  //Cascade Dependency Map
  const dependencyMap = useRef(new Map<string, string[]>());

  const handleExpand = (expanded: boolean) => {
    setExpanded(expanded);
  };

  const createRequestData = async () => {
    const data = {
      IncludeValidValues: true,
      IncludeValidValuesExceptions: [],
      IsReadOnly: true,
      ValidValueFilters: [],
      WindowName: "mob_inventory_issue_acct",
      ContextPKey: {
        Plant: await getCurrentPlant(),
        WoBase: "",
        WoTask: "  ",
        WoSubtask: "  ",
      },
    };
    return data;
  };

  useEffect(() => {
    (async () => {
      const data = await createRequestData();
      const response = await getColumnsWithGroup(data);
      if (response.status !== 200) {
        setMetadataError(true);
      } else if (response.data) {
        // Set metadata and values
        let fields: ColumnsWithGroupAndFilteredFields[] = [];
        let values: { [key: string]: string } = {};
        let keys = Object.keys(response.data.fields);
        keys.forEach((key) => {
          const fieldValue = response.data.fields[key];
          fields.push({
            ...fieldValue,
            filteredValues: fieldValue.ValidValues,
          });
          let propName = fieldValue.PropertyName;
          values[propName] = "";
        });
        fields.sort((a, b) => {
          return Number(a.FieldOrder) - Number(b.FieldOrder);
        });
        dependencyMap.current = getCascadeDependencyMap(fields);
        const cascadeValues = await getStoreAccountCascadeValues(
          fields.map((f) => f.PropertyName ?? "")
        );

        setCascadeValues(cascadeValues);
        setMetadata(fields);
        setValues(values);
      }
    })();
  }, []);

  const onIssueClick = () => {
    let issueDetails = "";
    let keyVals = Object.entries(values);
    keyVals.forEach((innerArray) => {
      if (
        innerArray.length > 1 &&
        innerArray[1] !== "" &&
        innerArray[1] !== null &&
        innerArray[1] !== undefined
      ) {
        issueDetails = `${issueDetails.length > 0 ? `${issueDetails}+` : ""}${
          innerArray[0]
        }+${innerArray[1]}`;
      }
    });
    router.push(`/Stores/Accounts/${encodeParam(issueDetails)}/${storesTab}`);
  };

  const updateCascadeChildOptions = (
    child: ColumnsWithGroupAndFilteredFields,
    cascadeValues: CascadeValue[],
    value: string
  ) => {
    const cascadedChildOptions = cascadeValues.filter(
      (c) => c.ParentValue === value
    );
    const filteredOptions = (child.ValidValues ?? []).filter((value) =>
      cascadedChildOptions.some((child) => value.Code === child.ChildValue)
    );

    const updatedOptions =
      filteredOptions.length > 0 ? filteredOptions : child.ValidValues;

    setMetadata((pre) => {
      return pre.map((p) => {
        if (p.PropertyName === child.PropertyName)
          return {
            ...p,
            filteredValues: updatedOptions,
          } as ColumnsWithGroupAndFilteredFields;
        return p;
      });
    });
  };

  return (
    <IonCard>
      <CardExpandableHeader
        expanded={expanded}
        onExpandClick={handleExpand}
        title={translations["lbl_account"] || "Account"}
      />
      <IonCardContent hidden={!expanded} style={{ paddingTop: 0 }}>
        {metadataError && (
          <IonLabel color="danger">
            {translatedMessages["ErrRetrievingFields"]?.MessageText ||
              "Error retrieving the fields for the form"}
          </IonLabel>
        )}
        <IonList>
          {metadata.map((field, index) => {
            const {
              PropertyName,
              TranslatedIdText,
              CascadeChild,
              filteredValues,
            } = field;
            const name = PropertyName ?? "";
            let currVal = values[name];
            let selectVal = filteredValues?.find((validVal: any) => {
              return validVal.Code === currVal;
            });
            return (
              <IonItem key={`stores-account-${index}`}>
                <IonLabel position="floating">{TranslatedIdText}</IonLabel>
                <IonSelect
                  interface="popover"
                  value={currVal}
                  selectedText={!!selectVal ? selectVal.Description : ""}
                  placeholder={translations["lbl_btn_select"] || "Select"}
                  onIonChange={(event) => {
                    const { value } = event.detail;
                    const child = metadata.find(
                      (m) => m.PropertyName === CascadeChild
                    );

                    if (CascadeChild && child && cascadeValues[name]?.length) {
                      updateCascadeChildOptions(
                        child,
                        cascadeValues[name],
                        value
                      );
                    }
                    let cascadedDataToRest: { [key: string]: string } = {};
                    const cascadeChild = getDependentObjects(
                      name,
                      dependencyMap.current
                    );
                    cascadeChild.forEach((c) => {
                      cascadedDataToRest = { ...cascadedDataToRest, [c]: "" };
                    });

                    setValues((pre) => ({
                      ...pre,
                      ...cascadedDataToRest,
                      [name]: value,
                    }));
                    PropertyName !== "Project" && setButtonDisabled(false); //Enable search only when an Account/Area/Department is selected.
                  }}
                >
                  {filteredValues?.map((value: any) => {
                    return (
                      <IonSelectOption key={value.Code} value={value.Code}>
                        {value.Description}
                      </IonSelectOption>
                    );
                  })}
                </IonSelect>
              </IonItem>
            );
          })}
          <IonButton
            color="primary"
            disabled={buttonDisabled}
            expand="full"
            onClick={onIssueClick}
          >
            {translations["lbl_btn_next"] || "Next"}
          </IonButton>
        </IonList>
      </IonCardContent>
    </IonCard>
  );
};

export default StoresAccount;
